'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    AiUserPromptLayer,
    AiUserPromptPreview,
    AiUserPromptSection,
    AiUserPromptVersion,
    createAiUserPromptSection,
    getAiUserPromptPreview,
    getAiUserPromptSections,
    getAiUserPromptVersions,
    restoreAiUserPromptVersion,
    saveAiUserPromptContent,
    updateAiUserPromptSection,
} from '@/lib/api/master/master-ai-user-prompt-api'
import { useEffect, useMemo, useState } from 'react'
import {
    MdAdd,
    MdHistory,
    MdRestore,
    MdSave,
    MdVisibility,
} from 'react-icons/md'
import { toast } from 'react-toastify'

/** Mesmo limite do backend (`AI_USER_PROMPT_SECTION_MAX_CHARS`). */
const SECTION_MAX_CHARS = 20_000

const LAYER_LABEL: Record<AiUserPromptLayer, string> = {
    PLATFORM: 'Plataforma (vale para todas as funções)',
    ROLE: 'Função: vendas',
}

const LAYER_ORDER: AiUserPromptLayer[] = ['PLATFORM', 'ROLE']

function formatDateTime(value: string): string {
    return new Date(value).toLocaleString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
    })
}

/**
 * Prompt do usuário de IA, editado sem deploy (docs/ai-user.md, "Prompt no banco").
 * Salvar põe a versão no ar na hora; o histórico guarda todas e volta qualquer uma.
 */
export default function AiUserPromptPanel() {
    const [sections, setSections] = useState<AiUserPromptSection[]>([])
    const [loading, setLoading] = useState(true)
    const [selectedId, setSelectedId] = useState<number | null>(null)
    const [pendingSelectId, setPendingSelectId] = useState<number | null>(null)
    const [draft, setDraft] = useState('')
    const [note, setNote] = useState('')
    const [saving, setSaving] = useState(false)
    const [versions, setVersions] = useState<AiUserPromptVersion[] | null>(null)
    const [loadingVersions, setLoadingVersions] = useState(false)
    const [expandedVersionId, setExpandedVersionId] = useState<number | null>(
        null
    )
    const [restoreTarget, setRestoreTarget] =
        useState<AiUserPromptVersion | null>(null)
    const [restoring, setRestoring] = useState(false)
    const [titleDraft, setTitleDraft] = useState('')
    const [orderDraft, setOrderDraft] = useState('')
    const [preview, setPreview] = useState<AiUserPromptPreview | null>(null)
    const [loadingPreview, setLoadingPreview] = useState(false)
    const [showCreate, setShowCreate] = useState(false)

    const selected = sections.find(section => section.id === selectedId) ?? null
    const dirty = selected != null && draft !== selected.content

    const load = async (keepSelection?: number | null) => {
        setLoading(true)
        try {
            const res = await getAiUserPromptSections()
            if (!res.success || !res.result) {
                toast.error(res.error || 'Erro ao carregar o prompt')
                return
            }
            setSections(res.result)
            const next =
                res.result.find(section => section.id === keepSelection) ??
                res.result[0] ??
                null
            select(next)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        void load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    function select(section: AiUserPromptSection | null) {
        setSelectedId(section?.id ?? null)
        setDraft(section?.content ?? '')
        setNote('')
        setVersions(null)
        setExpandedVersionId(null)
        setTitleDraft(section?.title ?? '')
        setOrderDraft(section ? String(section.sortOrder) : '')
    }

    const requestSelect = (section: AiUserPromptSection) => {
        if (section.id === selectedId) return
        if (dirty) {
            setPendingSelectId(section.id)
            return
        }
        select(section)
    }

    const grouped = useMemo(
        () =>
            LAYER_ORDER.map(layer => ({
                layer,
                sections: sections.filter(section => section.layer === layer),
            })),
        [sections]
    )

    const loadVersions = async (sectionId: number) => {
        setLoadingVersions(true)
        try {
            const res = await getAiUserPromptVersions(sectionId)
            if (res.success && res.result) setVersions(res.result)
            else toast.error(res.error || 'Erro ao carregar o histórico')
        } finally {
            setLoadingVersions(false)
        }
    }

    const handleSave = async () => {
        if (!selected) return
        if (!note.trim()) {
            toast.error('Diga o motivo da mudança: ele fica no histórico.')
            return
        }
        setSaving(true)
        try {
            const res = await saveAiUserPromptContent(selected.id, {
                content: draft,
                note: note.trim(),
            })
            if (res.success) {
                toast.success(
                    'Seção salva e no ar. Vale a partir do próximo passo da IA.'
                )
                await load(selected.id)
            } else {
                toast.error(res.error || 'Erro ao salvar a seção')
            }
        } finally {
            setSaving(false)
        }
    }

    const handleRestore = async () => {
        if (!selected || !restoreTarget) return
        setRestoring(true)
        try {
            const res = await restoreAiUserPromptVersion(
                selected.id,
                restoreTarget.id
            )
            if (res.success) {
                toast.success('Versão de volta ao ar.')
                setRestoreTarget(null)
                await load(selected.id)
                await loadVersions(selected.id)
            } else {
                toast.error(res.error || 'Erro ao voltar a versão')
            }
        } finally {
            setRestoring(false)
        }
    }

    const handleUpdateCustom = async (data: {
        title?: string
        enabled?: boolean
        sortOrder?: number
    }) => {
        if (!selected) return
        const res = await updateAiUserPromptSection(selected.id, data)
        if (res.success) {
            toast.success('Seção atualizada.')
            setSections(prev =>
                prev.map(section =>
                    section.id === selected.id
                        ? { ...section, ...data }
                        : section
                )
            )
            if (data.sortOrder !== undefined) await load(selected.id)
        } else {
            toast.error(res.error || 'Erro ao atualizar a seção')
        }
    }

    const openPreview = async () => {
        setLoadingPreview(true)
        try {
            const res = await getAiUserPromptPreview()
            if (res.success && res.result) setPreview(res.result)
            else toast.error(res.error || 'Erro ao montar a prévia')
        } finally {
            setLoadingPreview(false)
        }
    }

    if (loading && sections.length === 0) {
        return (
            <div className="flex items-center justify-center min-h-[200px]">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
            </div>
        )
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <p className="text-sm text-base-content/60 max-w-2xl">
                    Prompt do usuário de IA para todas as contas. Salvar põe a
                    versão no ar na hora, a partir do próximo passo; o histórico
                    guarda todas. O que é de cada conta continua nas instruções
                    do cadastro do usuário de IA.
                </p>
                <div className="flex gap-2 shrink-0">
                    <button
                        className="btn btn-sm btn-outline gap-1"
                        onClick={openPreview}
                        disabled={loadingPreview}
                    >
                        {loadingPreview ? (
                            <span className="loading loading-spinner loading-xs" />
                        ) : (
                            <MdVisibility />
                        )}
                        Prévia
                    </button>
                    <button
                        className="btn btn-sm btn-secondary gap-1"
                        onClick={() => setShowCreate(true)}
                    >
                        <MdAdd /> Nova seção
                    </button>
                </div>
            </div>

            <div className="space-y-4">
                {grouped.map(group => (
                    <div key={group.layer}>
                        <div className="text-xs font-semibold uppercase tracking-wide text-base-content/50 mb-2">
                            {LAYER_LABEL[group.layer]}
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {group.sections.map(section => (
                                <button
                                    key={section.id}
                                    type="button"
                                    className={`btn btn-sm ${
                                        section.id === selectedId
                                            ? 'btn-secondary'
                                            : 'btn-ghost border border-base-300'
                                    } ${section.enabled ? '' : 'opacity-50'}`}
                                    onClick={() => requestSelect(section)}
                                    title={section.description ?? undefined}
                                >
                                    {section.title}
                                    {section.isCustom && (
                                        <span className="badge badge-xs badge-outline">
                                            livre
                                        </span>
                                    )}
                                    {section.kind === 'step' && (
                                        <span className="badge badge-xs badge-outline">
                                            passo
                                        </span>
                                    )}
                                    {!section.enabled && (
                                        <span className="badge badge-xs">
                                            desligada
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {selected && (
                <div className="border-t border-base-300 pt-6 space-y-5">
                    <div>
                        <h3 className="text-lg font-semibold">
                            {selected.title}
                        </h3>
                        {selected.description && (
                            <p className="text-sm text-base-content/60 mt-1">
                                {selected.description}
                            </p>
                        )}
                        <p className="text-xs text-base-content/40 mt-2">
                            {selected.updatedAt
                                ? `No ar desde ${formatDateTime(selected.updatedAt)}${
                                      selected.updatedBy
                                          ? `, por ${selected.updatedBy.name}`
                                          : ''
                                  } · ${selected.versionCount} versão(ões)`
                                : 'Ainda sem versão no banco: usando o texto do código.'}
                        </p>
                    </div>

                    {selected.isCustom && (
                        <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px_auto] gap-4 items-end">
                            <div className="form-control w-full">
                                <label
                                    className="label py-1"
                                    htmlFor="ai-prompt-title"
                                >
                                    <span className="label-text text-sm font-medium">
                                        Título
                                    </span>
                                </label>
                                <input
                                    id="ai-prompt-title"
                                    className="input input-bordered w-full"
                                    value={titleDraft}
                                    maxLength={120}
                                    onChange={e =>
                                        setTitleDraft(e.target.value)
                                    }
                                    onBlur={() =>
                                        titleDraft.trim() &&
                                        titleDraft.trim() !== selected.title &&
                                        void handleUpdateCustom({
                                            title: titleDraft.trim(),
                                        })
                                    }
                                />
                            </div>
                            <div className="form-control w-full">
                                <label
                                    className="label py-1"
                                    htmlFor="ai-prompt-order"
                                >
                                    <span className="label-text text-sm font-medium">
                                        Ordem
                                    </span>
                                </label>
                                <input
                                    id="ai-prompt-order"
                                    type="number"
                                    min={0}
                                    className="input input-bordered w-full"
                                    value={orderDraft}
                                    onChange={e =>
                                        setOrderDraft(e.target.value)
                                    }
                                    onBlur={() => {
                                        const value = Number(orderDraft)
                                        if (
                                            Number.isInteger(value) &&
                                            value >= 0 &&
                                            value !== selected.sortOrder
                                        ) {
                                            void handleUpdateCustom({
                                                sortOrder: value,
                                            })
                                        }
                                    }}
                                />
                            </div>
                            <label className="label cursor-pointer justify-start gap-3 h-12">
                                <input
                                    type="checkbox"
                                    className="toggle toggle-secondary"
                                    checked={selected.enabled}
                                    onChange={e =>
                                        void handleUpdateCustom({
                                            enabled: e.target.checked,
                                        })
                                    }
                                />
                                <span className="label-text text-sm">
                                    {selected.enabled ? 'Ligada' : 'Desligada'}
                                </span>
                            </label>
                        </div>
                    )}

                    <div className="rounded-lg bg-base-200/60 p-3">
                        <div className="text-xs font-medium text-base-content/70 mb-2">
                            Marcadores desta seção
                        </div>
                        <ul className="space-y-1">
                            {selected.placeholders.map(placeholder => (
                                <li
                                    key={placeholder.name}
                                    className="text-xs text-base-content/70"
                                >
                                    <code className="font-mono text-secondary">{`{{${placeholder.name}}}`}</code>
                                    {placeholder.required && (
                                        <span className="badge badge-xs badge-warning ml-2">
                                            obrigatório
                                        </span>
                                    )}
                                    <span className="ml-2">
                                        {placeholder.description}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="form-control w-full">
                        <div className="flex items-center justify-between py-1">
                            <label
                                className="label-text text-sm font-medium"
                                htmlFor="ai-prompt-content"
                            >
                                Texto
                            </label>
                            <span
                                className={`text-xs ${
                                    draft.length > SECTION_MAX_CHARS
                                        ? 'text-error font-medium'
                                        : 'text-base-content/50'
                                }`}
                            >
                                {draft.length.toLocaleString('pt-BR')} /{' '}
                                {SECTION_MAX_CHARS.toLocaleString('pt-BR')}{' '}
                                caracteres
                            </span>
                        </div>
                        <textarea
                            id="ai-prompt-content"
                            className="textarea textarea-bordered w-full min-h-[420px] font-mono text-xs leading-relaxed"
                            value={draft}
                            onChange={e => setDraft(e.target.value)}
                            disabled={saving}
                            spellCheck={false}
                        />
                        <span className="text-xs text-base-content/50 mt-1">
                            Cada caractere vai em todos os passos de todas as
                            conversas. Conteúdo que cresce (preparo, regras por
                            parceiro) é para a base de conhecimento, não para o
                            prompt.
                        </span>
                    </div>

                    <div className="form-control w-full">
                        <label className="label py-1" htmlFor="ai-prompt-note">
                            <span className="label-text text-sm font-medium">
                                Motivo da mudança
                            </span>
                        </label>
                        <input
                            id="ai-prompt-note"
                            className="input input-bordered w-full"
                            value={note}
                            maxLength={500}
                            onChange={e => setNote(e.target.value)}
                            placeholder="Ex.: a IA oferecia parcelas com juros sem o contato perguntar"
                            disabled={saving}
                        />
                    </div>

                    <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3 pt-2">
                        <div className="flex flex-wrap gap-2">
                            <button
                                className="btn btn-sm btn-ghost gap-1"
                                onClick={() => {
                                    if (versions) setVersions(null)
                                    else void loadVersions(selected.id)
                                }}
                                disabled={loadingVersions}
                            >
                                {loadingVersions ? (
                                    <span className="loading loading-spinner loading-xs" />
                                ) : (
                                    <MdHistory />
                                )}
                                {versions ? 'Esconder histórico' : 'Histórico'}
                            </button>
                            {selected.defaultContent != null &&
                                draft !== selected.defaultContent && (
                                    <button
                                        className="btn btn-sm btn-ghost gap-1"
                                        onClick={() =>
                                            setDraft(selected.defaultContent!)
                                        }
                                        title="Põe no campo o texto original do código. Só vai ao ar se você salvar."
                                    >
                                        <MdRestore /> Texto do código
                                    </button>
                                )}
                            {dirty && (
                                <button
                                    className="btn btn-sm btn-ghost"
                                    onClick={() => setDraft(selected.content)}
                                >
                                    Descartar alterações
                                </button>
                            )}
                        </div>
                        <button
                            className="btn btn-secondary gap-1"
                            onClick={handleSave}
                            disabled={
                                saving ||
                                !dirty ||
                                draft.length > SECTION_MAX_CHARS
                            }
                        >
                            {saving ? (
                                <span className="loading loading-spinner loading-sm" />
                            ) : (
                                <MdSave />
                            )}
                            Salvar e pôr no ar
                        </button>
                    </div>

                    {versions && (
                        <div className="space-y-2">
                            <div className="text-sm font-medium">Histórico</div>
                            {versions.map((version, index) => (
                                <div
                                    key={version.id}
                                    className="rounded-lg border border-base-300 p-3"
                                >
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                        <div className="text-sm">
                                            <span className="font-medium">
                                                {formatDateTime(
                                                    version.createdAt
                                                )}
                                            </span>
                                            <span className="text-base-content/60">
                                                {' '}
                                                ·{' '}
                                                {version.createdBy?.name ??
                                                    'Sistema'}
                                            </span>
                                            {index === 0 && (
                                                <span className="badge badge-xs badge-success ml-2">
                                                    no ar
                                                </span>
                                            )}
                                            <div className="text-xs text-base-content/60 mt-1">
                                                {version.note}
                                            </div>
                                        </div>
                                        <div className="flex gap-2 shrink-0">
                                            <button
                                                className="btn btn-xs btn-ghost"
                                                onClick={() =>
                                                    setExpandedVersionId(
                                                        expandedVersionId ===
                                                            version.id
                                                            ? null
                                                            : version.id
                                                    )
                                                }
                                            >
                                                {expandedVersionId ===
                                                version.id
                                                    ? 'Esconder texto'
                                                    : 'Ver texto'}
                                            </button>
                                            {index > 0 && (
                                                <button
                                                    className="btn btn-xs btn-outline gap-1"
                                                    onClick={() =>
                                                        setRestoreTarget(
                                                            version
                                                        )
                                                    }
                                                >
                                                    <MdRestore /> Voltar a esta
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    {expandedVersionId === version.id && (
                                        <pre className="mt-3 max-h-80 overflow-auto whitespace-pre-wrap rounded bg-base-200 p-3 font-mono text-xs">
                                            {version.content}
                                        </pre>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <ConfirmModal
                isOpen={pendingSelectId != null}
                onClose={() => setPendingSelectId(null)}
                onConfirm={() => {
                    select(
                        sections.find(
                            section => section.id === pendingSelectId
                        ) ?? null
                    )
                    setPendingSelectId(null)
                }}
                title="Descartar alterações?"
                message="O texto desta seção foi alterado e não foi salvo. Trocar de seção descarta a alteração."
                confirmButtonText="Descartar e trocar"
            />

            <ConfirmModal
                isOpen={restoreTarget != null}
                onClose={() => setRestoreTarget(null)}
                onConfirm={handleRestore}
                loading={restoring}
                title="Voltar a esta versão?"
                message={
                    restoreTarget
                        ? `O texto de ${formatDateTime(restoreTarget.createdAt)} volta ao ar agora, como uma versão nova. O histórico não muda.`
                        : ''
                }
                confirmButtonText="Voltar e pôr no ar"
                confirmButtonClass="btn btn-secondary !border-0 !rounded-lg"
            />

            {preview && (
                <dialog className="modal modal-open z-50">
                    <div className="modal-box !max-w-4xl">
                        <h3 className="text-lg font-bold">Prévia do prompt</h3>
                        <p className="text-sm text-base-content/60 mt-1 mb-4">
                            Como o modelo recebe hoje, sem as instruções de cada
                            conta e as orientações do supervisor. Prompt de
                            sistema:{' '}
                            {preview.systemChars.toLocaleString('pt-BR')}{' '}
                            caracteres.
                        </p>
                        <div className="space-y-4">
                            <div>
                                <div className="text-sm font-medium mb-1">
                                    Prompt de sistema
                                </div>
                                <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded bg-base-200 p-3 font-mono text-xs">
                                    {preview.systemPrompt}
                                </pre>
                            </div>
                            {preview.steps.map(step => (
                                <div key={step.step}>
                                    <div className="text-sm font-medium mb-1">
                                        {step.title}
                                    </div>
                                    <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded bg-base-200 p-3 font-mono text-xs">
                                        {step.content}
                                    </pre>
                                </div>
                            ))}
                        </div>
                        <div className="modal-action">
                            <button
                                className="btn"
                                onClick={() => setPreview(null)}
                            >
                                Fechar
                            </button>
                        </div>
                    </div>
                    <div
                        className="modal-backdrop"
                        onClick={() => setPreview(null)}
                    />
                </dialog>
            )}

            {showCreate && (
                <CreateSectionModal
                    onClose={() => setShowCreate(false)}
                    onCreated={async id => {
                        setShowCreate(false)
                        await load(id)
                    }}
                />
            )}
        </div>
    )
}

function CreateSectionModal({
    onClose,
    onCreated,
}: {
    onClose: () => void
    onCreated: (id: number) => void
}) {
    const [title, setTitle] = useState('')
    const [layer, setLayer] = useState<AiUserPromptLayer>('ROLE')
    const [content, setContent] = useState('')
    const [note, setNote] = useState('')
    const [saving, setSaving] = useState(false)

    const canSave =
        title.trim() &&
        content.trim() &&
        note.trim() &&
        content.length <= SECTION_MAX_CHARS

    const handleCreate = async () => {
        if (!canSave) return
        setSaving(true)
        try {
            const res = await createAiUserPromptSection({
                title: title.trim(),
                layer,
                content,
                note: note.trim(),
            })
            if (res.success && res.result) {
                toast.success('Seção criada e no ar.')
                onCreated(res.result.id)
            } else {
                toast.error(res.error || 'Erro ao criar a seção')
            }
        } finally {
            setSaving(false)
        }
    }

    return (
        <dialog className="modal modal-open z-50">
            <div className="modal-box !max-w-2xl">
                <h3 className="text-lg font-bold">Nova seção</h3>
                <p className="text-sm text-base-content/60 mt-1 mb-4">
                    Entra no prompt de sistema depois das seções fixas da camada
                    escolhida. Use para uma regra de comportamento curta; o
                    único marcador é {'{{name}}'}.
                </p>
                <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="form-control w-full">
                            <label
                                className="label py-1"
                                htmlFor="ai-prompt-new-title"
                            >
                                <span className="label-text text-sm font-medium">
                                    Título
                                </span>
                            </label>
                            <input
                                id="ai-prompt-new-title"
                                className="input input-bordered w-full"
                                value={title}
                                maxLength={120}
                                onChange={e => setTitle(e.target.value)}
                                placeholder="Ex.: Política de reembolso"
                                disabled={saving}
                            />
                        </div>
                        <div className="form-control w-full">
                            <label
                                className="label py-1"
                                htmlFor="ai-prompt-new-layer"
                            >
                                <span className="label-text text-sm font-medium">
                                    Camada
                                </span>
                            </label>
                            <select
                                id="ai-prompt-new-layer"
                                className="select select-bordered w-full"
                                value={layer}
                                onChange={e =>
                                    setLayer(
                                        e.target.value as AiUserPromptLayer
                                    )
                                }
                                disabled={saving}
                            >
                                {LAYER_ORDER.map(value => (
                                    <option key={value} value={value}>
                                        {LAYER_LABEL[value]}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="form-control w-full">
                        <label
                            className="label py-1"
                            htmlFor="ai-prompt-new-content"
                        >
                            <span className="label-text text-sm font-medium">
                                Texto
                            </span>
                        </label>
                        <textarea
                            id="ai-prompt-new-content"
                            className="textarea textarea-bordered w-full min-h-48 font-mono text-xs leading-relaxed"
                            value={content}
                            onChange={e => setContent(e.target.value)}
                            disabled={saving}
                            spellCheck={false}
                        />
                    </div>
                    <div className="form-control w-full">
                        <label
                            className="label py-1"
                            htmlFor="ai-prompt-new-note"
                        >
                            <span className="label-text text-sm font-medium">
                                Motivo
                            </span>
                        </label>
                        <input
                            id="ai-prompt-new-note"
                            className="input input-bordered w-full"
                            value={note}
                            maxLength={500}
                            onChange={e => setNote(e.target.value)}
                            placeholder="Por que esta regra entra no prompt"
                            disabled={saving}
                        />
                    </div>
                </div>
                <div className="modal-action">
                    <button
                        className="btn btn-ghost"
                        onClick={onClose}
                        disabled={saving}
                    >
                        Cancelar
                    </button>
                    <button
                        className="btn btn-secondary gap-1"
                        onClick={handleCreate}
                        disabled={saving || !canSave}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm" />
                        ) : (
                            <MdAdd />
                        )}
                        Criar e pôr no ar
                    </button>
                </div>
            </div>
            <div
                className="modal-backdrop"
                onClick={() => !saving && onClose()}
            />
        </dialog>
    )
}
